import { useMemo, useState } from "react";
import { DataTable, type Column } from "../components/data/DataTable";
import { ParamForm } from "../components/data/ParamForm";
import { useLevelFilter } from "../components/data/LevelFilter";
import { Hint, NumInput } from "../components/ui/Field";
import { Notice } from "../components/ui/Notice";
import { Panel, SectionTitle } from "../components/ui/Panel";
import { Tag } from "../components/ui/Tag";
import { Amount } from "../components/ui/Amount";
import { DICE_LENS } from "../lib/apiLensIds";
import { toBig } from "../lib/big";
import { eMilliOf } from "../lib/curve";
import { levelRuntimeB, targetC, type Globals, type Level } from "../lib/model";
import { DEFAULT_DICE, IDLE_GROUP, type DiceParams } from "../lib/params";
import { useT } from "../lib/i18n";

interface Row { lv: Level; total: bigint | null; gMilli: bigint | null; perTapMilli: bigint | null; perTapMaxMilli: bigint | null; taps: bigint | null }

/** 千分之一金币 → 金币文本（最多两位小数，只显示） */
const milliText = (m: bigint | null): string => {
  if (m == null) return "—";
  if (m >= 100_000n) return (m / 1000n).toLocaleString("en-US");
  const cents = (m + 5n) / 10n;
  return `${cents / 100n}.${String(cents % 100n).padStart(2, "0")}`.replace(/\.?0+$/, "");
};

/**
 * 通关配置 · 挂机收益（2026-10-27，设计稿 idlepage.jsx）：玩家点投掷后、骰子动画还没结束时点屏幕，每下收本关每投金币的 p%。
 *
 * 只配两个数（存在 cfg_main_common，跟版本走）：p 与一次动画最多算几下；任一为 0 = 不开（生产不配就是关）。
 * 不配点击数曲线：只靠点击通关要点 = 本关总造价 ÷ (g × p)，后面的关骰次多，要点的次数自然跟着多。
 * 「平均每次点几下」只用于本页估算，不存、不下发。每投金币 g 与造价页同一口径：
 * 单掷金币版本取关上的 g，其余是 E × 本关收入 B（levelRuntimeB）；总造价取 targetC。全程 bigint。
 */
export function IdleIncomePage({
  dice, editable, onDice, levels, globals, costSource,
}: {
  dice: DiceParams;
  editable: boolean;
  onDice: (v: DiceParams) => void;
  levels: Level[];
  globals: Globals;
  costSource: string;
}) {
  const t = useT();
  const [avg, setAvg] = useState<number | null>(8);
  // 千分比：下限 / 上限；估算按区间中值（客户端均匀随机时的期望）
  const pml = dice.idlePct ?? 0, pmlMax = Math.max(pml, dice.idleMax ?? 0), cap = dice.idleCap ?? 0, on = pml > 0 && cap > 0;
  const bps = BigInt(Math.round(pml * 10)), bpsMax = BigInt(Math.round(pmlMax * 10));
  const mid = (pml + pmlMax) / 2;
  const share = (taps: number) => { const r = (taps * mid) / 1000; return r / (1 + r); };
  const shareText = (v: number) => `${(v * 100).toFixed(1)}%`;

  const m2 = globals.economyModel === 2;
  const rows: Row[] = useMemo(() => {
    const incomeBs = m2 ? [] : levelRuntimeB(levels, globals, costSource);
    const e = eMilliOf(globals.E);
    return levels.map((lv, i) => {
      const gMilli = m2 ? toBig(lv.gMilli ?? null) : incomeBs[i] != null && e > 0n ? incomeBs[i]! * e : null;
      const perTapMilli = gMilli != null ? (gMilli * bps) / 10_000n : null;
      const perTapMaxMilli = gMilli != null ? (gMilli * bpsMax) / 10_000n : null;
      const total = targetC(lv, globals);
      // 只靠点击通关的下数：总造价 ÷ 每下（区间中值），向上取整
      const avgMilli = perTapMilli != null && perTapMaxMilli != null ? (perTapMilli + perTapMaxMilli) / 2n : null;
      const taps = total != null && avgMilli != null && avgMilli > 0n ? (total * 1000n + avgMilli - 1n) / avgMilli : null;
      return { lv, total, gMilli, perTapMilli, perTapMaxMilli, taps };
    });
  }, [levels, globals, costSource, m2, bps, bpsMax]);
  const flt = useLevelFilter(levels);
  const shown = rows.filter((r) => flt.rows.some((l) => l.id === r.lv.id));
  const first = rows[0], last = rows[rows.length - 1];

  const columns: Column<Row>[] = [
    { key: "id", title: t("idle.col.level"), width: 200, render: (r) => `${r.lv.id}. ${r.lv.name}` },
    { key: "total", title: t("idle.col.total"), align: "right", render: (r) => <Amount v={r.total} /> },
    { key: "g", title: <span className="inline-flex items-center">{t("idle.col.g")}<Hint text={t("idle.col.gHint")} /></span>, align: "right", render: (r) => milliText(r.gMilli) },
    { key: "perTap", title: t("idle.col.perTap"), align: "right", render: (r) => r.perTapMilli === r.perTapMaxMilli ? milliText(r.perTapMilli) : `${milliText(r.perTapMilli)} ~ ${milliText(r.perTapMaxMilli)}` },
    { key: "taps", title: <span className="inline-flex items-center">{t("idle.col.taps")}<Hint text={t("idle.col.tapsHint")} /></span>, align: "right", render: (r) => <b><Amount v={r.taps} /></b> },
  ];

  return (
    <div className="flex w-full flex-col gap-page">
      <Panel>
        <SectionTitle extra={<Tag tone={on ? "success" : "info"}>{t(on ? "idle.on" : "idle.off")}</Tag>}>{t("nav.idle-income")}</SectionTitle>
        <Notice tone="info">{t("idle.note")}</Notice>
      </Panel>
      <div className="flex flex-wrap items-start gap-page">
        <div className="min-w-[320px] flex-1">
          <ParamForm groups={[IDLE_GROUP]} value={dice} editable={editable} onSave={onDice} defaults={DEFAULT_DICE} api={DICE_LENS} />
        </div>
        <Panel className="min-w-[300px]">
          <div className="mb-2 flex items-center gap-2 text-sm text-ink-regular">
            {t("idle.avg")}<Hint text={t("idle.avg.hint")} />
            <NumInput value={avg} onChange={(v) => setAvg(v == null ? null : Number(v))} className="w-[80px]" />{t("idle.u.taps")}
          </div>
          <div className="rounded-base bg-fill-light px-3 py-2 text-sm leading-7">
            <div>{t("idle.sum.perRoll", { n: mid > 0 ? Math.round(1000 / mid).toLocaleString("en-US") : "—" })}</div>
            <div>{t("idle.sum.avg", { n: avg ?? 0, v: shareText(share(avg ?? 0)) })}</div>
            <div>{t("idle.sum.cap", { n: cap, v: shareText(share(cap)) })}</div>
            {first && last && <div>{t("idle.sum.taps", { a: first.taps == null ? "—" : first.taps.toLocaleString("en-US"), n: last.lv.id, b: last.taps == null ? "—" : last.taps.toLocaleString("en-US") })}</div>}
          </div>
        </Panel>
      </div>
      <Panel>
        <SectionTitle>{t("idle.preview")}</SectionTitle>
        <div className="mb-3 flex flex-wrap items-center gap-2.5">{flt.filters}</div>
        <DataTable columns={columns} rows={shown} rowKey={(r) => r.lv.id} />
      </Panel>
    </div>
  );
}
